import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Download, Loader2, X, FileText } from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const CandidatContract = ({ candidate }) => {
  const [contracts, setContracts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [pdfModal, setPdfModal] = useState(null);

  const loadContracts = useCallback(() => {
    if (!candidate?.pld) return;
    setIsLoading(true);
    axios
      .get(`pld-contract/candidate/${candidate.pld}`)
      .then((res) => setContracts(res.data))
      .catch((e) => console.log(e))
      .finally(() => setIsLoading(false));
  }, [candidate?.pld]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const handleView = async (contract) => {
    const id = contract._id || contract.id;
    setActionLoading({ id, type: "view" });
    try {
      const res = await axios.get(`pld-contract/view/${id}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfModal({ url, name: contract.NumeroContrat || "Contrat" });
    } catch (e) {
      console.error("Erreur lors de la visualisation:", e);
      toast.error("Impossible d'ouvrir le contrat");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (contract) => {
    const id = contract._id || contract.id;
    setActionLoading({ id, type: "download" });
    try {
      const res = await axios.get(`pld-contract/download/${id}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrat_${contract.NumeroContrat || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Contrat téléchargé");
    } catch (e) {
      console.error("Erreur lors du téléchargement:", e);
      toast.error("Impossible de télécharger le contrat");
    } finally {
      setActionLoading(null);
    }
  };

  const closePdfModal = () => {
    if (pdfModal?.url) URL.revokeObjectURL(pdfModal.url);
    setPdfModal(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-slate-900">Contrats</h3>
            <p className="text-sm text-slate-500">
              {contracts.length} contrat{contracts.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
      </div>

      {contracts.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Aucun contrat disponible</p>
        </div>
      )}

      {contracts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    N° Contrat
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Poste
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Lieu
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contracts.map((contract, index) => {
                  const id = contract._id || contract.id;
                  return (
                    <motion.tr
                      key={id || index}
                      className="hover:bg-slate-50 transition-colors"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.03, 0.3) }}
                    >
                      <td className="px-5 py-4 text-sm text-slate-700">
                        {new Date(contract.createdAt).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-900">
                        {contract.NumeroContrat}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-700">
                        {contract.RaisonSociale}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-700">
                        {contract.TxtQualification}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-700">
                        {contract.AdresseVille}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-center space-x-1">
                          <motion.button
                            onClick={() => handleView(contract)}
                            disabled={actionLoading?.id === id && actionLoading?.type === "view"}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 disabled:opacity-50"
                            title="Voir le contrat"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            {actionLoading?.id === id && actionLoading?.type === "view" ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </motion.button>
                          <motion.button
                            onClick={() => handleDownload(contract)}
                            disabled={actionLoading?.id === id && actionLoading?.type === "download"}
                            className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 disabled:opacity-50"
                            title="Télécharger"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            {actionLoading?.id === id && actionLoading?.type === "download" ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      <AnimatePresence>
        {pdfModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePdfModal}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
                <h3 className="text-lg font-semibold text-slate-900">
                  {pdfModal.name}
                </h3>
                <motion.button
                  onClick={closePdfModal}
                  className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-5 h-5 text-slate-600" />
                </motion.button>
              </div>
              <div className="flex-1">
                <iframe
                  src={pdfModal.url}
                  className="w-full h-full"
                  title="Visualisation du contrat"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CandidatContract;
